import { SettingCategory } from './Setting';

/**
 * Extra vocabulary the search should accept for a setting, on top of its own label and help text.
 *
 * This is user-facing content, not logic: it exists because admins search for the words they use
 * with customers ("SMS", "2FA", "open rate") rather than the words the product happens to display
 * ("Texting", "Two Factor Authentication", "Track Opened Emails"). Support can extend these tables
 * without touching the matcher.
 *
 * Keys are the `SettingName` display strings, since that is what a built row carries as its
 * `labelText`. A key that no longer names a setting is inert rather than an error, so a renamed
 * setting silently loses its aliases — `settingsSearchAliases.spec.ts` guards against that.
 */
export const SETTING_ALIASES: Record<string, string[]> = {
	'AI Conditions in Automations': ['conditional', 'switch', 'automation segment'],
	'AI Email Campaign': ['ai campaign', 'generated campaign'],
	'AI Emails Per Campaign': ['ai campaign cap', 'ai limit', 'ai quota'],
	'AI Emails Per Month': ['ai monthly cap', 'ai limit', 'ai quota'],
	'AI Emails Per Month With Web Research': ['ai research cap', 'ai limit', 'ai quota'],
	'Allow OAuth Login': ['sso', 'single sign on', 'login', 'sign in'],
	'Allow Texting in Manually Triggered Automations': ['sms', 'text message'],
	'Allow Texting in Tag Automations': ['sms', 'text message'],
	'Compliance Email Address': ['approval', 'reviewer', 'supervision'],
	'Compliance enabled for emails, social and blogs': ['approval', 'supervision'],
	'Daily Limit': ['throttle', 'rate limit', 'cap', 'quota'],
	'Daily Scan Quota': ['throttle', 'rate limit', 'cap'],
	'Daily Send Limit': ['sms', 'text message', 'throttle', 'rate limit', 'cap', 'quota'],
	'Enabled OAuth Providers': ['sso', 'single sign on', 'login', 'sign in'],
	'Force compliance for all campaigns': ['approval', 'supervision'],
	'Hourly Send Limit': ['sms', 'text message', 'throttle', 'rate limit', 'cap', 'quota'],
	'HTML Newsletters': ['newsletter', 'mailing list', 'bulk email'],
	'Manage Newsletter Settings': ['newsletter', 'mailing list', 'bulk email'],
	'Maximum Daily Email Sends': ['throttle', 'rate limit', 'cap', 'quota'],
	'Maximum Group Automation Contacts': ['throttle', 'rate limit', 'cap', 'quota'],
	'Maximum Group Send Contacts': ['throttle', 'rate limit', 'cap', 'quota'],
	'Maximum HTML Newsletter Contacts': ['throttle', 'rate limit', 'cap', 'quota', 'newsletter'],
	'Only Allow OAuth Login': ['sso', 'single sign on', 'login', 'sign in'],
	'Seconds Between Sends': ['throttle', 'rate limit', 'cap'],
	'Send Bday/Financial Review Email': ['bday', 'birthday', 'date of birth'],
	'Send Bday/Renewal Email': ['bday', 'birthday', 'date of birth'],
	'Send Birthday Email': ['bday', 'date of birth'],
	'Send compliance confirmation emails': ['approval', 'supervision'],
	'Show Upcoming Birthdays to': ['bday', 'date of birth'],
	'Suppress Contacts Removed from Key Date Sends': ['opt out', 'unsubscribe', 'do not contact'],
	'Suppress Contacts Removed from Renewal': ['opt out', 'unsubscribe', 'do not contact'],
	Texting: ['sms', 'text message', 'text messaging'],
	'Texting Campaign Automation Trigger': ['sms', 'text message'],
	'Track Clicked Emails': ['click rate', 'clicks', 'click through'],
	'Track Opened Emails': ['open rate', 'opens', 'read receipt'],
	'Two Factor Authentication': ['2fa', 'mfa', 'tfa', 'multi factor', 'otp', 'authenticator'],
	'Use Parent Texting Campaign Registration': ['sms', 'text message'],
};

/**
 * Vocabulary that reaches every setting in a group. The group's own name is already searchable,
 * so these are only the words it is asked for by rather than called.
 */
export const CATEGORY_ALIASES: Partial<Record<SettingCategory, string[]>> = {
	[SettingCategory.AI]: ['artificial intelligence', 'agent', 'assistant', 'copilot'],
	[SettingCategory.Administrative]: ['admin', 'permissions', 'roles', 'access'],
	[SettingCategory.Dashboard]: ['home', 'cards', 'widgets'],
	[SettingCategory.Databoards]: ['data boards', 'reports', 'reporting', 'boards'],
	[SettingCategory.EmailTracking]: ['engagement', 'deliverability'],
	[SettingCategory.Features]: ['feature flag', 'toggles'],
	[SettingCategory.Permanent]: ['read only', 'locked', 'account creation'],
	[SettingCategory.PreApprovedContent]: ['compliance', 'approval', 'review', 'supervision'],
};
